import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import { useUserPrefs } from '../../Components/context/UserPrefs';
import { api, setCurrentPageContext } from '../../services/api';
import { BarChart3, Download, Filter, Timer, Image as ImageIcon, Truck, ClipboardCheck, Users, Wrench, Gauge, Fuel, CarFront, ListChecks, ShieldCheck } from 'lucide-react';
import SectionIndexCards from '../../Components/Common/SectionIndexCards';
import { Modal } from '../../Components/Modal/Modal';
import VehiclesModule from './VehiclesModule';
import PreoperacionalesModule from './PreoperacionalesModule';
import ConductoresModule from './ConductoresModule';
import MantenimientosModule from './MantenimientosModule';
import AsignacionVehiculosModule from './AsignacionVehiculosModule';
import PesvModule from './PesvModule';
import recordOperationsDownload from '../../utils/recordOperationsDownload';

function toDateTime(dateStr, timeStr) {
  const d = String(dateStr || '').trim();
  const t = String(timeStr || '').trim();
  if (!d || !t) return null;
  const tFixed = t.length >= 5 ? t.slice(0, 5) : t;
  const dt = new Date(`${d}T${tFixed}:00`);
  if (Number.isNaN(dt.getTime())) return null;
  return dt;
}

function maintenanceDurationMinutes(row) {
  const startDate = row?.start_date || row?.date;
  const endDate = row?.end_date || startDate;
  const start = toDateTime(startDate, row?.start_time);
  const end0 = toDateTime(endDate, row?.end_time);
  if (!start || !end0) return null;
  let end = end0;
  if (end.getTime() < start.getTime()) end = new Date(end.getTime() + 24 * 60 * 60 * 1000);
  const diff = Math.round((end.getTime() - start.getTime()) / 60000);
  if (!Number.isFinite(diff)) return null;
  return Math.max(0, diff);
}

function formatDuration(minutes) {
  if (minutes === null || minutes === undefined) return '—';
  const min = Math.round(Number(minutes) || 0);
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (h <= 0) return `${m} min`;
  if (m === 0) return `${h} h`;
  return `${h} h ${m} min`;
}

function IndicadoresMantenimientosModule() {
  const [vehicles, setVehicles] = useState([]);
  const [selectedVehicleId, setSelectedVehicleId] = useState('all');
  const [dateStart, setDateStart] = useState('');
  const [dateEnd, setDateEnd] = useState('');
  const [loadingVehicles, setLoadingVehicles] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [loadingTankings, setLoadingTankings] = useState(false);
  const [tankings, setTankings] = useState([]);

  const TYPES = useMemo(() => (['Preventivo', 'Correctivo', 'Otro']), []);
  const [selectedTypes, setSelectedTypes] = useState(['Preventivo', 'Correctivo', 'Otro']);
  const typeMeta = useMemo(() => ({
    Preventivo: { label: 'Preventivos', color: 'bg-green-600', stroke: '#16a34a' },
    Correctivo: { label: 'Correctivos', color: 'bg-orange-600', stroke: '#F07E2D' },
    Otro: { label: 'Otros', color: 'bg-gray-600', stroke: '#4b5563' },
  }), []);

  useEffect(() => {
    const loadVehicles = async () => {
      setLoadingVehicles(true);
      try {
        const res = await api.maintenances.get();
        if (res.ok) setVehicles(res.data || []);
      } catch (_) {
      } finally {
        setLoadingVehicles(false);
      }
    };
    loadVehicles();
  }, []);

  useEffect(() => {
    const loadStats = async () => {
      setLoading(true);
      try {
        const filters = selectedVehicleId === 'all' ? {} : { vehicle_id: Number(selectedVehicleId) || 0 };
        const res = await api.maintenances.stats(filters);
        if (res.ok) setRows(res.data || []);
        else alert(res.error || 'No fue posible cargar los indicadores.');
      } catch (_) {
        alert('No fue posible cargar los indicadores.');
      } finally {
        setLoading(false);
      }
    };
    loadStats();
  }, [selectedVehicleId]);

  const selectedVehicle = useMemo(() => {
    if (selectedVehicleId === 'all') return null;
    const id = Number(selectedVehicleId) || 0;
    return (vehicles || []).find((v) => Number(v?.id) === id) || null;
  }, [selectedVehicleId, vehicles]);

  const fmtMoney = (n) => {
    const v = Number(n || 0);
    try {
      return v.toLocaleString('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 });
    } catch (_) {
      return '$' + String(Math.round(v)).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    }
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoadingTankings(true);
      try {
        const start = String(dateStart || '').trim();
        const end = String(dateEnd || '').trim();
        const from = start || end || '';
        const to = end || start || '';
        const res = await api.preoperationals.tankings({
          ...(from ? { from } : {}),
          ...(to ? { to } : {}),
          limit: 2000,
          order: 'desc',
        });
        const data = Array.isArray(res?.data) ? res.data : [];
        const mapped = data.map((r) => ({
          plate: String(r?.plate ?? r?.placa ?? '').trim().toUpperCase(),
          amount: (r?.amount === null || r?.amount === undefined || r?.amount === '') ? 0 : Number(r.amount) || 0,
        })).filter((x) => x.plate);
        if (alive) setTankings(mapped);
      } catch (_) {
        if (alive) setTankings([]);
      } finally {
        if (alive) setLoadingTankings(false);
      }
    })();
    return () => { alive = false; };
  }, [dateStart, dateEnd]);

  const normalizedRows = useMemo(() => {
    const arr = Array.isArray(rows) ? rows : [];
    return arr.map((r) => {
      const minutes = maintenanceDurationMinutes(r);
      return { ...r, __duration_minutes: minutes };
    });
  }, [rows]);

  const normalizeType = useCallback((rawType) => {
    const t = String(rawType || '').trim();
    return TYPES.includes(t) ? t : 'Otro';
  }, [TYPES]);

  const isDateInRange = (dateStr, startStr, endStr) => {
    const d = String(dateStr || '').trim();
    if (!d) return false;
    const start = String(startStr || '').trim();
    const end = String(endStr || '').trim();
    if (!start && !end) return true;
    if (start && d < start) return false;
    if (end && d > end) return false;
    return true;
  };

  const dateFilteredRows = useMemo(() => {
    const start = String(dateStart || '').trim();
    const end = String(dateEnd || '').trim();
    return (normalizedRows || []).filter((r) => {
      const d = r?.start_date || r?.date;
      return isDateInRange(d, start, end);
    });
  }, [dateEnd, dateStart, normalizedRows]);

  const filteredRows = useMemo(() => {
    const active = Array.isArray(selectedTypes) && selectedTypes.length ? selectedTypes : TYPES;
    const activeSet = new Set(active);
    return (dateFilteredRows || []).filter((r) => activeSet.has(normalizeType(r?.type)));
  }, [TYPES, dateFilteredRows, selectedTypes, normalizeType]);

  const buildSummary = useCallback((sourceRows) => {
    const byType = {};
    TYPES.forEach((t) => {
      byType[t] = { type: t, count: 0, minutes: 0, withTime: 0, cost: 0 };
    });
    let total = 0;
    let minutesTotal = 0;
    let withTimeTotal = 0;
    let costTotal = 0;

    (sourceRows || []).forEach((r) => {
      const type = normalizeType(r?.type);
      byType[type].count += 1;
      total += 1;
      const cost = Number(r?.cost) || 0;
      byType[type].cost += cost;
      costTotal += cost;
      const m = r?.__duration_minutes;
      if (m !== null && m !== undefined) {
        byType[type].minutes += Number(m) || 0;
        byType[type].withTime += 1;
        minutesTotal += Number(m) || 0;
        withTimeTotal += 1;
      }
    });

    const maxCount = Math.max(1, ...TYPES.map((t) => byType[t]?.count || 0));
    const maxMinutes = Math.max(1, ...TYPES.map((t) => byType[t]?.minutes || 0));
    const maxCost = Math.max(1, ...TYPES.map((t) => byType[t]?.cost || 0));
    return { byType, total, minutesTotal, withTimeTotal, costTotal, maxCount, maxMinutes, maxCost };
  }, [TYPES, normalizeType]);

  const baseSummary = useMemo(() => buildSummary(dateFilteredRows), [dateFilteredRows, buildSummary]);
  const summary = useMemo(() => buildSummary(filteredRows), [filteredRows, buildSummary]);

  const byPlate = useMemo(() => {
    const map = new Map();
    filteredRows.forEach((r) => {
      const plate = String(r?.plate || '').trim() || '—';
      const key = plate;
      const current = map.get(key) || {
        plate,
        total: 0,
        minutes: 0,
        withTime: 0,
        Preventivo: 0,
        Correctivo: 0,
        Otro: 0,
      };
      current.total += 1;
      const t = normalizeType(r?.type);
      current[t] += 1;
      const m = r?.__duration_minutes;
      if (m !== null && m !== undefined) {
        current.minutes += Number(m) || 0;
        current.withTime += 1;
      }
      map.set(key, current);
    });
    return Array.from(map.values()).sort((a, b) => String(a.plate).localeCompare(String(b.plate)));
  }, [filteredRows, normalizeType]);

  const tankingByPlate = useMemo(() => {
    const map = new Map();
    (Array.isArray(tankings) ? tankings : []).forEach((t) => {
      const plate = String(t?.plate || '').trim().toUpperCase();
      if (!plate) return;
      map.set(plate, (map.get(plate) || 0) + (Number(t?.amount || 0) || 0));
    });
    return map;
  }, [tankings]);

  const tankingRowsForKpi = useMemo(() => {
    const all = Array.isArray(tankings) ? tankings : [];
    if (selectedVehicleId === 'all') return all;
    const p = String(selectedVehicle?.plate || '').trim().toUpperCase();
    if (!p) return all;
    return all.filter((x) => String(x.plate || '').trim().toUpperCase() === p);
  }, [selectedVehicleId, selectedVehicle, tankings]);

  const tankingTotalValue = useMemo(() => (tankingRowsForKpi || []).reduce((acc, x) => acc + (Number(x?.amount || 0) || 0), 0), [tankingRowsForKpi]);

  const selectedTypesSet = useMemo(() => new Set((Array.isArray(selectedTypes) && selectedTypes.length ? selectedTypes : TYPES)), [TYPES, selectedTypes]);

  const toggleType = (type) => {
    if (type === 'all') {
      setSelectedTypes([...TYPES]);
      return;
    }
    setSelectedTypes((prev) => {
      const current = Array.isArray(prev) && prev.length ? prev : [...TYPES];
      const set = new Set(current);
      if (set.has(type)) set.delete(type);
      else set.add(type);
      const next = TYPES.filter((t) => set.has(t));
      return next.length ? next : [...TYPES];
    });
  };

  const pieSlices = useMemo(() => {
    const total = summary.total || 0;
    if (!total) return [];
    const values = TYPES.map((t) => ({ type: t, value: summary.byType?.[t]?.count || 0 }));
    const filtered = values.filter((v) => v.value > 0);
    const out = [];
    let acc = 0;
    filtered.forEach((v) => {
      const start = acc / total;
      acc += v.value;
      const end = acc / total;
      out.push({ ...v, start, end, pct: Math.round((v.value / total) * 100) });
    });
    return out;
  }, [TYPES, summary]);

  const monthly = useMemo(() => {
    const monthKeyOf = (dateStr) => {
      const d = String(dateStr || '').trim();
      if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) return null;
      return d.slice(0, 7);
    };
    const parseDate = (d) => {
      const s = String(d || '').trim();
      if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
      const dt = new Date(`${s}T00:00:00`);
      return Number.isNaN(dt.getTime()) ? null : dt;
    };
    const toMonthStart = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
    const addMonths = (d, n) => new Date(d.getFullYear(), d.getMonth() + n, 1);

    const source = dateFilteredRows || [];
    const startDt = parseDate(dateStart);
    const endDt = parseDate(dateEnd);
    if (!source.length && !startDt && !endDt) return { series: [], maxCount: 1 };
    const dates = source
      .map((r) => parseDate(r?.start_date || r?.date))
      .filter(Boolean);
    const minData = dates.length ? new Date(Math.min(...dates.map((d) => d.getTime()))) : null;
    const maxData = dates.length ? new Date(Math.max(...dates.map((d) => d.getTime()))) : null;

    const from = toMonthStart(startDt || minData || new Date());
    const to = toMonthStart(endDt || maxData || from);

    const months = [];
    for (let i = 0; i < 240; i++) {
      const m = addMonths(from, i);
      if (m.getTime() > to.getTime()) break;
      const key = `${m.getFullYear()}-${String(m.getMonth() + 1).padStart(2, '0')}`;
      months.push({ key, label: key, Preventivo: 0, Correctivo: 0 });
    }

    const countsPrev = new Map();
    const countsCorr = new Map();
    source.forEach((r) => {
      const key = monthKeyOf(r?.start_date || r?.date);
      if (!key) return;
      const t = normalizeType(r?.type);
      if (t === 'Preventivo') countsPrev.set(key, (countsPrev.get(key) || 0) + 1);
      if (t === 'Correctivo') countsCorr.set(key, (countsCorr.get(key) || 0) + 1);
    });

    const series = months.map((m) => ({
      ...m,
      Preventivo: countsPrev.get(m.key) || 0,
      Correctivo: countsCorr.get(m.key) || 0,
    }));
    const maxCount = Math.max(1, ...series.map((s) => Math.max(s.Preventivo || 0, s.Correctivo || 0)));
    return { series, maxCount };
  }, [dateEnd, dateStart, dateFilteredRows, normalizeType]);

  return (
    <div className="p-4 space-y-6">
      <div className="bg-white p-4 rounded-xl shadow border border-gray-200 flex flex-col md:flex-row gap-4 justify-between items-center">
        <div className="flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-orange-600" />
          <div className="text-lg font-semibold">Indicadores de Mantenimientos</div>
        </div>
        <div className="w-full md:w-auto flex flex-col md:flex-row gap-3 md:items-end">
          <div className="flex items-center gap-2 w-full md:w-auto">
            <div className="flex items-center gap-2 text-sm text-gray-600 whitespace-nowrap">
              <Filter className="w-4 h-4" />
              Vehículo
            </div>
            <select
              className="w-full md:w-56 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-orange-500"
              value={selectedVehicleId}
              onChange={(e) => setSelectedVehicleId(e.target.value)}
              disabled={loadingVehicles}
            >
              <option value="all">Todos</option>
              {(vehicles || []).map((v) => (
                <option key={v.id} value={String(v.id)}>
                  {String(v.plate || '').trim() || `Vehículo ${v.id}`}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3 w-full md:w-auto">
            <div>
              <div className="text-xs text-gray-600 mb-1">Fecha inicio</div>
              <input
                type="date"
                className="w-full md:w-44 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-orange-500"
                value={dateStart}
                onChange={(e) => setDateStart(e.target.value)}
              />
            </div>
            <div>
              <div className="text-xs text-gray-600 mb-1">Fecha fin</div>
              <input
                type="date"
                className="w-full md:w-44 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-orange-500"
                value={dateEnd}
                onChange={(e) => setDateEnd(e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white p-4 rounded-xl shadow border border-gray-200">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="text-sm font-semibold">Filtros por tipo</div>
          <div className="text-xs text-gray-500">
            {selectedVehicle ? `Placa: ${selectedVehicle.plate}` : 'Todas las placas'} · {selectedTypesSet.size === TYPES.length ? 'Todos los tipos' : `${selectedTypesSet.size} tipo(s) seleccionados`}
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mt-3">
          <button
            type="button"
            onClick={() => toggleType('all')}
            className={`px-3 py-1 text-sm rounded-full border ${selectedTypesSet.size === TYPES.length ? 'bg-gray-900 text-white border-gray-900' : 'bg-gray-100 text-gray-800 border-gray-300 hover:bg-gray-200'}`}
          >
            Todos ({baseSummary.total})
          </button>
          {TYPES.map((t) => {
            const meta = typeMeta[t] || { label: t };
            const active = selectedTypesSet.has(t);
            const count = baseSummary.byType?.[t]?.count || 0;
            const btn = (() => {
              if (t === 'Preventivo') {
                return active
                  ? 'bg-green-600 text-white border-green-600'
                  : 'bg-green-50 text-green-800 border-green-200 hover:bg-green-100';
              }
              if (t === 'Correctivo') {
                return active
                  ? 'text-white border-orange-600'
                  : 'text-orange-800 border-orange-200 hover:bg-orange-100';
              }
              return active
                ? 'bg-gray-600 text-white border-gray-600'
                : 'bg-gray-100 text-gray-800 border-gray-300 hover:bg-gray-200';
            })();
            const style = (() => {
              if (t !== 'Correctivo') return undefined;
              if (active) return { backgroundColor: '#F07E2D', borderColor: '#F07E2D' };
              return { backgroundColor: '#FFF7ED', borderColor: '#FED7AA' };
            })();
            return (
              <button
                key={t}
                type="button"
                onClick={() => toggleType(t)}
                className={`px-3 py-1 text-sm rounded-full border ${btn}`}
                title={meta.label}
                style={style}
              >
                {meta.label} ({count})
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
        <div className="bg-white rounded-xl shadow border border-gray-200 p-4">
          <div className="text-xs text-gray-500">Mantenimientos</div>
          <div className="text-2xl font-semibold">{loading ? '—' : summary.total}</div>
          <div className="text-xs text-gray-500 mt-1">{selectedTypesSet.size === TYPES.length ? 'Sin filtro por tipo' : 'Filtrado por tipo'}</div>
        </div>
        <div className="bg-white rounded-xl shadow border border-gray-200 p-4">
          <div className="text-xs text-gray-500">Tiempo total (con horas)</div>
          <div className="text-2xl font-semibold">{loading ? '—' : formatDuration(summary.minutesTotal)}</div>
          <div className="text-xs text-gray-500 mt-1">{loading ? '—' : `${summary.withTimeTotal} con tiempo registrado`}</div>
        </div>
        <div className="bg-white rounded-xl shadow border border-gray-200 p-4">
          <div className="text-xs text-gray-500">{typeMeta.Preventivo.label}</div>
          <div className={`text-2xl font-semibold ${selectedTypesSet.has('Preventivo') ? '' : 'text-gray-400'}`}>{loading ? '—' : baseSummary.byType.Preventivo.count}</div>
          <div className="text-xs text-gray-500 mt-1">{loading ? '—' : formatDuration(baseSummary.byType.Preventivo.minutes)}</div>
        </div>
        <div className="bg-white rounded-xl shadow border border-gray-200 p-4">
          <div className="text-xs text-gray-500">{typeMeta.Correctivo.label}</div>
          <div className={`text-2xl font-semibold ${selectedTypesSet.has('Correctivo') ? '' : 'text-gray-400'}`}>{loading ? '—' : baseSummary.byType.Correctivo.count}</div>
          <div className="text-xs text-gray-500 mt-1">{loading ? '—' : formatDuration(baseSummary.byType.Correctivo.minutes)}</div>
        </div>
        <div className="bg-white rounded-xl shadow border border-gray-200 p-4">
          <div className="text-xs text-gray-500">{typeMeta.Otro.label}</div>
          <div className={`text-2xl font-semibold ${selectedTypesSet.has('Otro') ? '' : 'text-gray-400'}`}>{loading ? '—' : baseSummary.byType.Otro.count}</div>
          <div className="text-xs text-gray-500 mt-1">{loading ? '—' : formatDuration(baseSummary.byType.Otro.minutes)}</div>
        </div>
        <div className="bg-white rounded-xl shadow border border-gray-200 p-4">
          <div className="text-xs text-gray-500">Valor tanqueos</div>
          <div className="text-2xl font-semibold">{loadingTankings ? '—' : fmtMoney(tankingTotalValue)}</div>
          <div className="text-xs text-gray-500 mt-1">{loadingTankings ? '—' : `${(tankingRowsForKpi || []).length} registro(s)`}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl shadow border border-gray-200 p-4">
          <div className="text-sm font-semibold mb-3">Mantenimientos por tipo</div>
          {loading ? (
            <div className="py-10 text-center text-sm text-gray-500">Cargando...</div>
          ) : summary.total === 0 ? (
            <div className="py-10 text-center text-sm text-gray-500">Sin datos para mostrar.</div>
          ) : (
            <div className="space-y-3">
              {TYPES.map((t) => {
                const item = summary.byType[t];
                const pct = Math.round(((item.count || 0) / summary.maxCount) * 100);
                const meta = typeMeta[t] || { label: t, stroke: '#4b5563' };
                return (
                  <div key={t}>
                    <div className="flex items-center justify-between text-xs text-gray-700">
                      <span>{meta.label}</span>
                      <span className="font-semibold">{item.count}</span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded mt-1 overflow-hidden">
                      <div className="h-2" style={{ width: `${pct}%`, backgroundColor: meta.stroke || '#4b5563' }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl shadow border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-semibold">Tiempo usado por tipo</div>
            <div className="flex items-center gap-1 text-xs text-gray-500">
              <Timer className="w-4 h-4" />
              Solo registros con hora inicio/fin
            </div>
          </div>
          {loading ? (
            <div className="py-10 text-center text-sm text-gray-500">Cargando...</div>
          ) : summary.withTimeTotal === 0 ? (
            <div className="py-10 text-center text-sm text-gray-500">No hay mantenimientos con horas registradas.</div>
          ) : (
            <div className="space-y-3">
              {TYPES.map((t) => {
                const item = summary.byType[t];
                const pct = Math.round(((item.minutes || 0) / summary.maxMinutes) * 100);
                const meta = typeMeta[t] || { label: t, stroke: '#4b5563' };
                return (
                  <div key={t}>
                    <div className="flex items-center justify-between text-xs text-gray-700">
                      <span>{meta.label}</span>
                      <span className="font-semibold">{formatDuration(item.minutes)}</span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded mt-1 overflow-hidden">
                      <div className="h-2" style={{ width: `${pct}%`, backgroundColor: meta.stroke || '#4b5563' }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl shadow border border-gray-200 p-4">
          <div className="text-sm font-semibold mb-3">Distribución por tipo (torta)</div>
          {loading ? (
            <div className="py-10 text-center text-sm text-gray-500">Cargando...</div>
          ) : summary.total === 0 ? (
            <div className="py-10 text-center text-sm text-gray-500">Sin datos para mostrar.</div>
          ) : (
            <div className="flex flex-col md:flex-row gap-4 items-center">
              <svg viewBox="0 0 120 120" className="w-40 h-40">
                <circle cx="60" cy="60" r="46" fill="none" stroke="#e5e7eb" strokeWidth="18" />
                {(() => {
                  const r = 46;
                  const c = 2 * Math.PI * r;
                  return pieSlices.map((s) => {
                    const dash = Math.max(0, (s.end - s.start) * c);
                    const offset = c * (1 - s.start);
                    const stroke = typeMeta?.[s.type]?.stroke || '#4b5563';
                    return (
                      <circle
                        key={s.type}
                        cx="60"
                        cy="60"
                        r={r}
                        fill="none"
                        stroke={stroke}
                        strokeWidth="18"
                        strokeDasharray={`${dash} ${c - dash}`}
                        strokeDashoffset={offset}
                        strokeLinecap="butt"
                        transform="rotate(-90 60 60)"
                      >
                        <title>{`${typeMeta?.[s.type]?.label || s.type}: ${s.value} (${s.pct}%)`}</title>
                      </circle>
                    );
                  });
                })()}
                <circle cx="60" cy="60" r="30" fill="white" />
                <text x="60" y="60" textAnchor="middle" dominantBaseline="central" fontSize="16" fill="#111827" fontWeight="600">
                  {summary.total}
                </text>
                <text x="60" y="76" textAnchor="middle" fontSize="10" fill="#6b7280">
                  Registros
                </text>
              </svg>
              <div className="flex-1 w-full">
                <div className="space-y-2">
                  {TYPES.map((t) => {
                    const item = summary.byType?.[t];
                    const value = item?.count || 0;
                    const pct = summary.total ? Math.round((value / summary.total) * 100) : 0;
                    const stroke = typeMeta?.[t]?.stroke || '#4b5563';
                    return (
                      <div key={t} className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2">
                          <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: stroke }} />
                          <span className="text-gray-700">{typeMeta?.[t]?.label || t}</span>
                        </div>
                        <div className="text-gray-900 font-semibold">{value} <span className="text-gray-500 font-normal">({pct}%)</span></div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl shadow border border-gray-200 p-4">
          <div className="text-sm font-semibold mb-1">Diagrama de puntos: mantenimientos por mes</div>
          <div className="text-xs text-gray-500 mb-3">Cada punto es un mes. El valor es la cantidad de mantenimientos registrados en ese mes.</div>
          {loading ? (
            <div className="py-10 text-center text-sm text-gray-500">Cargando...</div>
          ) : monthly.series.length === 0 ? (
            <div className="py-10 text-center text-sm text-gray-500">Sin datos para mostrar.</div>
          ) : (
            <div className="w-full overflow-hidden">
              {(() => {
                const w = 640;
                const h = 260;
                const pad = { l: 56, r: 18, t: 16, b: 44 };
                const x0 = pad.l;
                const y0 = pad.t;
                const x1 = w - pad.r;
                const y1 = h - pad.b;
                const series = monthly.series;
                const maxY = monthly.maxCount || 1;
                const showPrev = selectedTypesSet.has('Preventivo');
                const showCorr = selectedTypesSet.has('Correctivo');
                const yScale = (v) => {
                  const p = maxY <= 0 ? 0 : (Number(v) || 0) / maxY;
                  return y1 - p * (y1 - y0);
                };
                const n = series.length;
                const xScaleIdx = (i) => {
                  if (n <= 1) return x0;
                  const p = i / (n - 1);
                  return x0 + p * (x1 - x0);
                };
                const yTicks = (() => {
                  const ticks = [];
                  const steps = Math.min(5, maxY);
                  for (let i = 0; i <= steps; i++) ticks.push(Math.round((maxY * i) / steps));
                  return Array.from(new Set(ticks));
                })();
                const labelStep = Math.max(1, Math.ceil(n / 8));
                const linePointsPrev = series.map((s, i) => `${xScaleIdx(i)},${yScale(s.Preventivo || 0)}`).join(' ');
                const linePointsCorr = series.map((s, i) => `${xScaleIdx(i)},${yScale(s.Correctivo || 0)}`).join(' ');
                return (
                  <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-[260px]">
                    <rect x="0" y="0" width={w} height={h} fill="white" />
                    <rect x={x0} y={y0} width={x1 - x0} height={y1 - y0} fill="#fafafa" />
                    {yTicks.map((t) => (<line key={t} x1={x0} y1={yScale(t)} x2={x1} y2={yScale(t)} stroke="#e5e7eb" />))}
                    <line x1={x0} y1={y1} x2={x1} y2={y1} stroke="#d1d5db" />
                    <line x1={x0} y1={y0} x2={x0} y2={y1} stroke="#d1d5db" />
                    {yTicks.map((t) => (<text key={`t-${t}`} x={x0 - 8} y={yScale(t)} fontSize="10" fill="#6b7280" textAnchor="end" dominantBaseline="central">{t}</text>))}
                    <text x={12} y={22} fontSize="10" fill="#6b7280">Cantidad</text>
                    {showPrev && (
                      <polyline points={linePointsPrev} fill="none" stroke="#16a34a" strokeWidth="2" opacity="0.7" />
                    )}
                    {showCorr && (
                      <polyline points={linePointsCorr} fill="none" stroke="#F07E2D" strokeWidth="2" opacity="0.7" />
                    )}
                    {series.map((s, i) => {
                      const cx = xScaleIdx(i);
                      const pv = s.Preventivo || 0;
                      const cv = s.Correctivo || 0;
                      const title = `${s.label}\nPreventivos: ${pv}\nCorrectivos: ${cv}`;
                      return (
                        <g key={s.key}>
                          {showPrev && (
                            <circle cx={cx} cy={yScale(pv)} r={4} fill="#16a34a" fillOpacity="0.95">
                              <title>{title}</title>
                            </circle>
                          )}
                          {showCorr && (
                            <circle cx={cx} cy={yScale(cv)} r={4} fill="#F07E2D" fillOpacity="0.95">
                              <title>{title}</title>
                            </circle>
                          )}
                        </g>
                      );
                    })}
                    {series.map((s, i) => {
                      if (i % labelStep !== 0 && i !== n - 1) return null;
                      const x = xScaleIdx(i);
                      return (
                        <text key={`x-${s.key}`} x={x} y={h - 14} fontSize="10" fill="#6b7280" textAnchor="middle">
                          {s.label}
                        </text>
                      );
                    })}
                  </svg>
                );
              })()}
              <div className="flex flex-wrap gap-4 mt-2 text-xs text-gray-600">
                {selectedTypesSet.has('Preventivo') && (
                  <div className="flex items-center gap-2">
                    <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: '#16a34a' }} />
                    <span>Preventivos</span>
                  </div>
                )}
                {selectedTypesSet.has('Correctivo') && (
                  <div className="flex items-center gap-2">
                    <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: '#F07E2D' }} />
                    <span>Correctivos</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {selectedVehicleId === 'all' ? (
        <div className="bg-white rounded-xl shadow border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 border-b text-sm font-semibold">Resumen por placa</div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-gray-700 uppercase font-medium">
                <tr>
                  <th className="px-4 py-3 text-left">Placa</th>
                  <th className="px-4 py-3 text-right">Total</th>
                  <th className="px-4 py-3 text-right">Preventivo</th>
                  <th className="px-4 py-3 text-right">Correctivo</th>
                  <th className="px-4 py-3 text-right">Otro</th>
                  <th className="px-4 py-3 text-right">Tiempo</th>
                  <th className="px-4 py-3 text-right">Valor tanqueos</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {loading ? (
                  <tr><td colSpan="7" className="px-4 py-10 text-center text-gray-500">Cargando...</td></tr>
                ) : byPlate.length === 0 ? (
                  <tr><td colSpan="7" className="px-4 py-10 text-center text-gray-500">Sin datos.</td></tr>
                ) : (
                  byPlate.map((r) => (
                    <tr key={r.plate}>
                      <td className="px-4 py-3 font-medium text-gray-900">{r.plate}</td>
                      <td className="px-4 py-3 text-right">{r.total}</td>
                      <td className="px-4 py-3 text-right">{r.Preventivo}</td>
                      <td className="px-4 py-3 text-right">{r.Correctivo}</td>
                      <td className="px-4 py-3 text-right">{r.Otro}</td>
                      <td className="px-4 py-3 text-right">{r.withTime ? formatDuration(r.minutes) : '—'}</td>
                      <td className="px-4 py-3 text-right font-mono">
                        {loadingTankings
                          ? '—'
                          : (() => {
                              const k = String(r.plate || '').trim().toUpperCase();
                              return tankingByPlate.has(k) ? fmtMoney(tankingByPlate.get(k) || 0) : '—';
                            })()}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 border-b text-sm font-semibold">Detalle de mantenimientos</div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-gray-700 uppercase font-medium">
                <tr>
                  <th className="px-4 py-3 text-left">Fecha</th>
                  <th className="px-4 py-3 text-left">Tipo</th>
                  <th className="px-4 py-3 text-left">Duración</th>
                  <th className="px-4 py-3 text-left">Descripción</th>
                  <th className="px-4 py-3 text-right">Kilometraje/Odómetro</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {loading ? (
                  <tr><td colSpan="5" className="px-4 py-10 text-center text-gray-500">Cargando...</td></tr>
                ) : filteredRows.length === 0 ? (
                  <tr><td colSpan="5" className="px-4 py-10 text-center text-gray-500">Sin datos.</td></tr>
                ) : (
                  filteredRows.map((h) => {
                    const s = h.start_date || h.date;
                    const e = h.end_date;
                    const t1 = h.start_time;
                    const t2 = h.end_time;
                    const datePart = (e && e !== s) ? `${s} - ${e}` : `${s}`;
                    const timePart = (t1 || t2) ? ` (${t1 || '--:--'} - ${t2 || '--:--'})` : '';
                    return (
                      <tr key={h.id}>
                        <td className="px-4 py-3">{`${datePart}${timePart}`}</td>
                        <td className="px-4 py-3">{h.type}</td>
                        <td className="px-4 py-3">{formatDuration(h.__duration_minutes)}</td>
                        <td className="px-4 py-3">{h.description || '-'}</td>
                        <td className="px-4 py-3 text-right">{h.mileage ?? '-'}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function ControlKilometrajeModule() {
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.preoperationals.latestMileage();
      const data = Array.isArray(res?.data) ? res.data : [];
      const mapped = data.map((x) => {
        const vehicle = x?.vehicle || null;
        const base = x?.base || null;
        const override = x?.override || null;
        const current = (x?.mileage !== null && x?.mileage !== undefined) ? String(x.mileage) : '';
        return { vehicle, base, override, mileageInput: current, saving: false };
      }).filter((x) => x.vehicle && x.vehicle.id);
      setRows(mapped);
    } catch (_) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const term = String(q || '').trim().toLowerCase();
    if (!term) return rows;
    return (rows || []).filter((r) => {
      const v = r.vehicle || {};
      const hay = [v.plate, v.brand, v.model].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(term);
    });
  }, [q, rows]);

  const setMileage = (vehicleId, value) => {
    setRows((prev) => (prev || []).map((r) => (
      String(r?.vehicle?.id) === String(vehicleId) ? { ...r, mileageInput: value } : r
    )));
  };

  const exportExcel = () => {
    const items = (rows || []).map((r) => {
      const vehicle = r?.vehicle || {};
      const base = r?.base || null;
      const override = r?.override || null;
      const baseMileage = (base?.mileage !== null && base?.mileage !== undefined) ? Number(base.mileage) : null;
      const overrideMileage = (override?.mileage !== null && override?.mileage !== undefined) ? Number(override.mileage) : null;
      const reportedMileage = overrideMileage !== null ? overrideMileage : baseMileage;
      const reportDate = String(base?.date || '').trim() || (override?.updated_at ? String(override.updated_at).slice(0, 10) : '');
      const overrideAt = override?.updated_at ? String(override.updated_at).slice(0, 16).replace('T', ' ') : '';
      return {
        vehiculo: [vehicle?.plate, [vehicle?.brand, vehicle?.model].filter(Boolean).join(' ')].filter(Boolean).join(' - '),
        placa: vehicle?.plate || '',
        marca: vehicle?.brand || '',
        modelo: vehicle?.model || '',
        fecha_reporte: reportDate,
        fuente: String(base?.source || (overrideMileage !== null ? 'ajuste manual' : '')).toLowerCase() === 'pdf' ? 'PDF' : (base?.source ? 'Formulario' : (overrideMileage !== null ? 'Ajuste manual' : '')),
        kilometraje_horometro: reportedMileage,
        kilometraje_pdf: baseMileage,
        ajuste_manual: overrideMileage,
        fecha_ajuste_manual: overrideAt,
      };
    }).filter((item) => item.fecha_reporte || item.kilometraje_horometro !== null);

    if (!items.length) {
      window.alert('No hay kilometrajes reportados para exportar.');
      return;
    }

    setExporting(true);
    try {
      const ws = XLSX.utils.json_to_sheet(items, {
        header: [
          'vehiculo',
          'placa',
          'marca',
          'modelo',
          'fecha_reporte',
          'fuente',
          'kilometraje_horometro',
          'kilometraje_pdf',
          'ajuste_manual',
          'fecha_ajuste_manual',
        ],
      });
      XLSX.utils.sheet_add_aoa(ws, [[
        'Vehículo',
        'Placa',
        'Marca',
        'Modelo',
        'Fecha de reporte',
        'Fuente',
        'Kilometraje/Horómetro',
        'Kilometraje PDF',
        'Ajuste manual',
        'Fecha ajuste manual',
      ]], { origin: 'A1' });
      ws['!cols'] = [
        { wch: 28 },
        { wch: 14 },
        { wch: 18 },
        { wch: 18 },
        { wch: 16 },
        { wch: 16 },
        { wch: 22 },
        { wch: 16 },
        { wch: 16 },
        { wch: 20 },
      ];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Kilometrajes');
      const stamp = new Date().toISOString().slice(0, 10);
      void recordOperationsDownload({ fileType: 'xlsx', moduleKey: 'transportes', reportName: 'Control de kilometraje' });
      XLSX.writeFile(wb, `control_kilometraje_${stamp}.xlsx`);
    } catch (e) {
      window.alert(e?.message || 'No fue posible exportar el Excel.');
    } finally {
      setExporting(false);
    }
  };

  const saveRow = async (vehicleId) => {
    const id = Number(vehicleId || 0) || 0;
    if (!id) return;
    const row = (rows || []).find((r) => Number(r?.vehicle?.id || 0) === id) || null;
    if (!row) return;
    const raw = String(row.mileageInput || '').trim();
    if (!raw) { window.alert('Ingresa el kilometraje.'); return; }
    const mileage = Math.max(0, Number(raw.replace(/\D+/g, '')) || 0);
    setRows((prev) => (prev || []).map((r) => (Number(r?.vehicle?.id || 0) === id ? { ...r, saving: true } : r)));
    try {
      await api.preoperationals.updateMileage({
        vehicle_id: id,
        mileage,
      });
      await load();
    } catch (e) {
      window.alert(e?.message || 'No fue posible guardar el kilometraje.');
      setRows((prev) => (prev || []).map((r) => (Number(r?.vehicle?.id || 0) === id ? { ...r, saving: false } : r)));
    }
  };

  return (
    <div className="bg-white rounded-xl shadow border border-gray-200 p-5">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-lg font-semibold">Control de kilometraje</div>
          <div className="text-xs text-gray-500">Toma el último kilometraje/horómetro del preoperacional (PDF). Si se ajusta manualmente, prevalece el ajuste.</div>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Filter className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="border rounded-lg pl-9 pr-3 py-2 text-sm bg-gray-50"
              placeholder="Filtrar por placa/marca/modelo"
            />
          </div>
          <button
            type="button"
            onClick={exportExcel}
            className="px-3 py-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-sm inline-flex items-center gap-2"
            disabled={loading || exporting || !rows.length}
          >
            <Download className="h-4 w-4" />
            {exporting ? 'Exportando...' : 'Exportar Excel'}
          </button>
          <button
            type="button"
            onClick={load}
            className="px-3 py-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-sm"
            disabled={loading}
          >
            Actualizar
          </button>
        </div>
      </div>

      <div className="mt-4 bg-white border border-gray-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="px-4 py-10 text-center text-sm text-gray-600">Cargando...</div>
        ) : (
          <>
            {filtered.length === 0 ? (
              <div className="px-4 py-10 text-center text-sm text-gray-600">Sin vehículos para mostrar.</div>
            ) : (
              <div className="overflow-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-gray-600">
                    <tr>
                      <th className="px-4 py-3 text-left">Vehículo</th>
                      <th className="px-4 py-3 text-left">Último preoperacional</th>
                      <th className="px-4 py-3 text-right">Kilometraje/Horómetro</th>
                      <th className="px-4 py-3 text-left">Acción</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {filtered.map((r) => {
                      const v = r.vehicle || {};
                      const baseDate = r.base?.date || '';
                      const baseMileage = r.base?.mileage;
                      const baseSource = String(r.base?.source || '').toLowerCase();
                      const overrideMileage = r.override?.mileage;
                      const overrideAt = r.override?.updated_at;
                      return (
                        <tr key={v.id}>
                          <td className="px-4 py-3">
                            <div className="font-medium">{v.plate}</div>
                            <div className="text-xs text-gray-500">{[v.brand, v.model].filter(Boolean).join(' · ') || '-'}</div>
                          </td>
                          <td className="px-4 py-3 text-gray-700">
                            {baseDate ? (
                              <div>
                                <div>{baseDate}</div>
                                <div className="text-xs text-gray-500">{baseSource === 'pdf' ? 'Fuente: PDF' : 'Fuente: formulario'}</div>
                              </div>
                            ) : (
                              <span className="text-gray-400">No registrado</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <input
                                value={r.mileageInput}
                                onChange={(e) => setMileage(v.id, e.target.value)}
                                className="w-32 border rounded-lg px-3 py-2 bg-gray-50 text-right"
                                placeholder={baseMileage !== null && baseMileage !== undefined ? String(baseMileage) : '0'}
                                inputMode="numeric"
                              />
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                              {baseMileage !== null && baseMileage !== undefined ? `PDF: ${baseMileage}` : 'PDF: —'}
                              {overrideMileage !== null && overrideMileage !== undefined ? ` · Ajuste: ${overrideMileage}${overrideAt ? ` (${String(overrideAt).slice(0, 16).replace('T', ' ')})` : ''}` : ''}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <button
                              type="button"
                              onClick={() => saveRow(v.id)}
                              className="px-3 py-2 rounded-lg text-white text-sm"
                              style={{ backgroundColor: '#2563EB' }}
                              disabled={!!r.saving}
                            >
                              {r.saving ? 'Guardando...' : 'Guardar'}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function tanqueoFileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    if (!file) { resolve(''); return; }
    const reader = new FileReader();
    reader.onloadend = () => resolve(String(reader.result || ''));
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// Modal de Control Tanqueos (panel web, Transportes/Admin): crea un
// tanqueo nuevo con fecha atrasada (la app del conductor solo puede
// registrar con la fecha/hora del momento) o edita uno existente. `row`
// viene de normalizeRow(); un `row.id` presente significa edición.
function TanqueoFormModal({ row, onClose, onSaved }) {
  const isEdit = !!row?.id;
  const [form, setForm] = useState({
    fecha: row?.__dateOnly || row?.date || '',
    placa: row?.plate || '',
    conductor: row?.conductor || '',
    tipo_vehiculo: row?.vehicle_type || '',
    centro_costo: row?.cost_center || '',
    municipio: row?.municipio || '',
    km_horometro: row?.mileage ?? '',
    numero_comprobante: row?.voucher_number || '',
    valor: row?.amount ?? '',
    tipo_combustible: row?.fuel_type || '',
    galones: row?.galones ?? '',
    eds: '',
    tipo_tanqueo: row?.tipo_tanqueo || '',
  });
  const [photos, setPhotos] = useState({ fotoKm: null, fotoTicket: null, fotoDispensador: null });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const combustibles = ['Gasolina', 'ACPM'];
  const edsOptions = ['TERPEL', 'BIOMAX', 'PRIMAX', 'TEXACO', 'PETROMIL'];
  const tipoTanqueoOptions = ['Parcial', 'Lleno'];
  const photoFields = [
    { id: 'fotoKm', label: 'Foto del kilometraje/horómetro', existing: row?.photo_km },
    { id: 'fotoTicket', label: 'Foto del ticket/comprobante', existing: row?.photo_ticket },
    { id: 'fotoDispensador', label: 'Foto del dispensador', existing: row?.photo_dispenser },
  ];

  const submit = async () => {
    setError('');
    if (!form.fecha || !form.placa.trim()) {
      setError('Fecha y placa son obligatorias.');
      return;
    }
    setSaving(true);
    try {
      const [fotoKmDataUrl, fotoTicketDataUrl, fotoDispensadorDataUrl] = await Promise.all([
        photos.fotoKm ? tanqueoFileToDataUrl(photos.fotoKm) : '',
        photos.fotoTicket ? tanqueoFileToDataUrl(photos.fotoTicket) : '',
        photos.fotoDispensador ? tanqueoFileToDataUrl(photos.fotoDispensador) : '',
      ]);
      const payload = {
        ...(isEdit ? { id: row.id } : {}),
        fecha: form.fecha,
        placa: form.placa.trim().toUpperCase(),
        conductor: form.conductor,
        tipo_vehiculo: form.tipo_vehiculo,
        centro_costo: form.centro_costo,
        municipio: form.municipio,
        km_horometro: form.km_horometro,
        numero_comprobante: form.numero_comprobante,
        valor: form.valor,
        tipo_combustible: form.tipo_combustible,
        galones: form.galones,
        eds: form.eds,
        tipo_tanqueo: form.tipo_tanqueo,
        foto_km_base64: fotoKmDataUrl,
        foto_ticket_base64: fotoTicketDataUrl,
        foto_dispensador_base64: fotoDispensadorDataUrl,
      };
      const res = await api.preoperationals.tanqueoUpsert(payload);
      if (!res?.ok) throw new Error(res?.error || 'No fue posible guardar el tanqueo.');
      await onSaved();
    } catch (e) {
      setError(e?.message || 'No fue posible guardar el tanqueo.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title={isEdit ? `Editar tanqueo · ${row?.plate || ''}` : 'Nuevo tanqueo'} size="max-w-lg">
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Fecha *</label>
            <input type="date" value={form.fecha} onChange={(e) => setForm((p) => ({ ...p, fecha: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Placa *</label>
            <input value={form.placa} onChange={(e) => setForm((p) => ({ ...p, placa: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="Ej: ABC123" />
          </div>
          <div className="col-span-2">
            <label className="block text-xs text-gray-600 mb-1">Conductor</label>
            <input value={form.conductor} onChange={(e) => setForm((p) => ({ ...p, conductor: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Centro de costo</label>
            <input value={form.centro_costo} onChange={(e) => setForm((p) => ({ ...p, centro_costo: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Municipio</label>
            <input value={form.municipio} onChange={(e) => setForm((p) => ({ ...p, municipio: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Km / horómetro</label>
            <input value={form.km_horometro} onChange={(e) => setForm((p) => ({ ...p, km_horometro: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Nº comprobante</label>
            <input value={form.numero_comprobante} onChange={(e) => setForm((p) => ({ ...p, numero_comprobante: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Valor</label>
            <input value={form.valor} onChange={(e) => setForm((p) => ({ ...p, valor: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Galones</label>
            <input value={form.galones} onChange={(e) => setForm((p) => ({ ...p, galones: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Tipo de combustible</label>
            <select value={form.tipo_combustible} onChange={(e) => setForm((p) => ({ ...p, tipo_combustible: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm">
              <option value="">Seleccione...</option>
              {combustibles.map((c) => (<option key={c} value={c}>{c}</option>))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Estación de servicio</label>
            <select value={form.eds} onChange={(e) => setForm((p) => ({ ...p, eds: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm">
              <option value="">Seleccione...</option>
              {edsOptions.map((c) => (<option key={c} value={c}>{c}</option>))}
            </select>
          </div>
          <div className="col-span-2">
            <label className="block text-xs text-gray-600 mb-1">Tipo de tanqueo</label>
            <select value={form.tipo_tanqueo} onChange={(e) => setForm((p) => ({ ...p, tipo_tanqueo: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm">
              <option value="">Seleccione...</option>
              {tipoTanqueoOptions.map((c) => (<option key={c} value={c}>{c}</option>))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-2">
          {photoFields.map((pf) => (
            <div key={pf.id}>
              <label className="flex items-center gap-2 text-sm border rounded-lg px-3 py-2 cursor-pointer">
                <ImageIcon size={16} />
                {photos[pf.id] ? photos[pf.id].name : (pf.existing ? 'Reemplazar foto' : pf.label)}
                <input type="file" accept="image/*" className="hidden" onChange={(e) => setPhotos((p) => ({ ...p, [pf.id]: e.target.files?.[0] || null }))} />
              </label>
              {(photos[pf.id] || pf.existing) && (
                <img src={photos[pf.id] ? URL.createObjectURL(photos[pf.id]) : pf.existing} alt={pf.label} className="w-full h-24 object-cover rounded-lg border mt-1" />
              )}
            </div>
          ))}
        </div>

        {error && <div className="px-3 py-2 rounded-lg border text-sm bg-red-50 border-red-200 text-red-700">{error}</div>}

        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border text-sm">Cancelar</button>
          <button
            type="button"
            disabled={saving}
            onClick={submit}
            className="px-4 py-2 rounded-lg text-white text-sm disabled:opacity-60"
            style={{ backgroundColor: '#EA580C' }}
          >
            {saving ? 'Guardando...' : isEdit ? 'Guardar cambios' : 'Registrar tanqueo'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function ControlTanqueosModule() {
  const toYMD = (d) => {
    if (!d) return '';
    const dt = new Date(d);
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, '0');
    const day = String(dt.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const todayYMD = useMemo(() => toYMD(new Date()), []);
  const currentMonth = useMemo(() => String(todayYMD || '').slice(0, 7), [todayYMD]);
  const monthRange = useCallback((monthValue) => {
    const month = String(monthValue || '').trim();
    if (!/^\d{4}-\d{2}$/.test(month)) return { from: todayYMD, to: todayYMD };
    const from = `${month}-01`;
    const to = toYMD(new Date(Number(month.slice(0, 4)), Number(month.slice(5, 7)), 0));
    return { from, to };
  }, [todayYMD]);

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [indicatorLoading, setIndicatorLoading] = useState(false);
  const [monthlyRows, setMonthlyRows] = useState([]);
  const [filters, setFilters] = useState({ from: todayYMD, to: todayYMD, q: '', plate: '', topMonth: currentMonth });
  const [tanqueoModalTarget, setTanqueoModalTarget] = useState(null);

  const resolveMediaUrl = useCallback((input) => {
    const u = String(input || '').trim();
    if (!u) return '';
    if (/^https?:\/\//i.test(u)) return u;
    if (/^data:/i.test(u)) return u;
    const origin = (typeof window !== 'undefined' && window.location && window.location.origin) ? window.location.origin : '';
    if (!origin) return u;
    if (u.startsWith('/')) return `${origin}${u}`;
    const path = (typeof window !== 'undefined' && window.location) ? String(window.location.pathname || '') : '';
    const base = /^\/senerfi(\/|$)/i.test(path) ? `${origin}/senerfi/` : `${origin}/`;
    const rel = u.replace(/^\/+/, '');
    return `${base}${rel}`;
  }, []);

  const parseMaybeJson = useCallback((v) => {
    if (!v) return null;
    if (typeof v === 'object') return v;
    const s = String(v || '').trim();
    if (!s) return null;
    if (!(s.startsWith('{') || s.startsWith('['))) return null;
    try {
      const out = JSON.parse(s);
      return out && typeof out === 'object' ? out : null;
    } catch (_) {
      return null;
    }
  }, []);

  const pickFirstUrl = useCallback((v) => {
    if (!v) return null;
    if (typeof v === 'string') {
      const s = v.trim();
      if (!s) return null;
      const decoded = parseMaybeJson(s);
      if (decoded) return pickFirstUrl(decoded);
      if (s.includes(',') && !/^data:/i.test(s)) {
        const parts = s.split(',').map((x) => x.trim()).filter(Boolean);
        if (parts.length) return parts[0];
      }
      return s;
    }
    if (Array.isArray(v)) {
      for (const item of v) {
        const u = pickFirstUrl(item);
        if (u) return u;
      }
      return null;
    }
    if (typeof v === 'object') {
      const url = (v.url ?? v.href ?? v.path ?? v.src);
      const u0 = pickFirstUrl(url);
      if (u0) return u0;
      for (const k of ['photo_km', 'foto_km', 'fotoKm', 'photo_ticket', 'foto_ticket', 'fotoTicket', 'photo_dispenser', 'foto_dispensador', 'fotoDispensador']) {
        const u1 = pickFirstUrl(v[k]);
        if (u1) return u1;
      }
      return null;
    }
    return null;
  }, [parseMaybeJson]);

  const toNumberOrNull = useCallback((v) => {
    if (v === null || v === undefined) return null;
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    const s = String(v).trim();
    if (!s) return null;
    const cleaned = s.replace(/,/g, '.');
    const m = cleaned.match(/-?\d+(\.\d+)?/);
    if (!m) return null;
    const n = Number(m[0]);
    return Number.isFinite(n) ? n : null;
  }, []);

  const normalizeRow = useCallback((r) => {
    const plate = String(r?.plate ?? r?.placa ?? r?.vehicle_plate ?? r?.vehiclePlate ?? '').trim();
    const rawDate = String(r?.date ?? r?.fecha ?? r?.created_at ?? r?.createdAt ?? '').trim();
    const dateOnly = rawDate ? rawDate.slice(0, 10) : '';
    const mileageRaw = (r?.mileage ?? r?.kilometraje_horometro ?? r?.kilometrajeHorometro ?? r?.km_horometro ?? r?.kmHorometro ?? r?.kilometraje ?? r?.horometro ?? r?.odometro ?? r?.km ?? r?.lectura);
    const mileage = toNumberOrNull(mileageRaw);
    const fuelType = String(r?.fuel_type ?? r?.tipo_combustible ?? r?.combustible ?? '').trim() || null;
    const costCenter = String(r?.cost_center ?? r?.centro_costo ?? r?.centro_de_costo ?? r?.cc ?? '').trim() || null;
    const voucherNumber = String(r?.voucher_number ?? r?.numero_comprobante ?? r?.comprobante ?? r?.nro_comprobante ?? '').trim() || null;
    const amountRaw = (r?.amount ?? r?.valor ?? r?.valor_tanqueo ?? r?.monto ?? r?.total);
    const amount = toNumberOrNull(amountRaw);
    const galonesRaw = (r?.galones ?? r?.galonaje ?? r?.gallons);
    const galones = toNumberOrNull(galonesRaw);
    const vehicleType = String(r?.vehicle_type ?? r?.vehicleType ?? '').trim() || null;
    const photoKmRaw = (r?.photo_km ?? r?.photoKm ?? r?.foto_km ?? r?.fotoKm ?? r?.fotoKM ?? r?.foto_kilometraje ?? r?.fotoKilometraje ?? r?.foto_odometro ?? r?.fotoOdometro ?? r?.foto_horometro ?? r?.fotoHorometro);
    const photoTicketRaw = (r?.photo_ticket ?? r?.photoTicket ?? r?.foto_ticket ?? r?.fotoTicket ?? r?.foto_comprobante ?? r?.fotoComprobante ?? r?.foto_factura ?? r?.fotoFactura ?? r?.foto_recibo ?? r?.fotoRecibo);
    const photoDispenserRaw = (r?.photo_dispenser ?? r?.photoDispenser ?? r?.foto_dispensador ?? r?.fotoDispensador ?? r?.foto_surtidor ?? r?.fotoSurtidor ?? r?.foto_dispensador_combustible ?? r?.fotoDispensadorCombustible);
    const photoKm = pickFirstUrl(photoKmRaw);
    const photoTicket = pickFirstUrl(photoTicketRaw);
    const photoDispenser = pickFirstUrl(photoDispenserRaw);
    const idRaw = r?.id;
    const id = (idRaw === null || idRaw === undefined || idRaw === '') ? null : Number(idRaw);
    return {
      id: Number.isFinite(id) ? id : null,
      conductor: String(r?.conductor ?? '').trim() || null,
      municipio: String(r?.municipio ?? '').trim() || null,
      tipo_tanqueo: String(r?.tipo_tanqueo ?? '').trim() || null,
      plate,
      date: dateOnly || rawDate || null,
      __dateOnly: dateOnly || null,
      __dateTime: rawDate || null,
      mileage: Number.isFinite(mileage) ? mileage : null,
      fuel_type: fuelType,
      cost_center: costCenter,
      voucher_number: voucherNumber,
      amount: Number.isFinite(amount) ? amount : null,
      galones: Number.isFinite(galones) ? galones : null,
      vehicle_type: vehicleType,
      photo_km: photoKm ? resolveMediaUrl(photoKm) : null,
      photo_ticket: photoTicket ? resolveMediaUrl(photoTicket) : null,
      photo_dispenser: photoDispenser ? resolveMediaUrl(photoDispenser) : null,
      source: r?.source ?? r?.fuente ?? null,
    };
  }, [pickFirstUrl, resolveMediaUrl, toNumberOrNull]);

  const dedupeTankings = (list) => {
    const score = (x) => {
      let s = 0;
      if (x?.mileage !== null && x?.mileage !== undefined) s += 2;
      if (x?.fuel_type) s += 1;
      if (x?.cost_center) s += 1;
      if (x?.voucher_number) s += 1;
      if (x?.amount !== null && x?.amount !== undefined) s += 1;
      if (x?.photo_km) s += 1;
      if (x?.photo_ticket) s += 1;
      if (x?.photo_dispenser) s += 1;
      return s;
    };
    const keyOf = (x) => {
      const p = String(x?.plate || '').trim().toUpperCase();
      const d = String(x?.__dateOnly || x?.date || '').trim().slice(0, 10);
      const voucher = String(x?.voucher_number || '').trim().toUpperCase();
      const amount = x?.amount !== null && x?.amount !== undefined ? String(x.amount) : '';
      const mileage = x?.mileage !== null && x?.mileage !== undefined ? String(x.mileage) : '';
      const time = String(x?.__dateTime || '').trim();
      return `${p}|${d}|${voucher}|${amount}|${mileage}|${time}`;
    };
    const map = new Map();
    (Array.isArray(list) ? list : []).forEach((x) => {
      const key = keyOf(x);
      if (!key || key === '|') return;
      const prev = map.get(key);
      if (!prev) { map.set(key, x); return; }
      const sp = score(prev);
      const sx = score(x);
      if (sx > sp) { map.set(key, x); return; }
      if (sx < sp) return;
      const tPrev = String(prev.__dateTime || prev.date || '');
      const tNow = String(x.__dateTime || x.date || '');
      if (tNow > tPrev) map.set(key, x);
    });
    return Array.from(map.values()).sort((a, b) => {
      const da = String(a.__dateTime || a.date || '');
      const db = String(b.__dateTime || b.date || '');
      if (da === db) return String(a.plate || '').localeCompare(String(b.plate || ''));
      return db.localeCompare(da);
    });
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // "Día" vacío = sin filtro de fecha (útil para buscar por placa en
      // cualquier fecha) — antes caía por defecto en el día de hoy, lo
      // que hacía imposible buscar una placa sin conocer la fecha exacta.
      const from = String(filters.from || '').trim();
      const to = String(filters.to || '').trim() || from;
      const res = await api.preoperationals.tankings({
        from,
        to,
        q: filters.q,
        plate: filters.plate,
        limit: 3000,
      });
      const data = Array.isArray(res?.data) ? res.data : [];
      const normalized = data.map(normalizeRow).filter((x) => x.plate && x.date);
      setRows(dedupeTankings(normalized));
    } catch (_) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [filters.from, filters.plate, filters.q, filters.to, normalizeRow]);

  const loadMonthlyTop = useCallback(async () => {
    setIndicatorLoading(true);
    try {
      const { from, to } = monthRange(filters.topMonth);
      const res = await api.preoperationals.tankings({
        from,
        to,
        limit: 5000,
      });
      const data = Array.isArray(res?.data) ? res.data : [];
      const normalized = data.map(normalizeRow).filter((x) => x.plate && x.date);
      setMonthlyRows(dedupeTankings(normalized));
    } catch (_) {
      setMonthlyRows([]);
    } finally {
      setIndicatorLoading(false);
    }
  }, [filters.topMonth, monthRange, normalizeRow]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    loadMonthlyTop();
  }, [loadMonthlyTop]);

  const fmtMoney = (n) => {
    const v = Number(n || 0);
    try {
      return v.toLocaleString('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 });
    } catch (_) {
      return '$' + String(Math.round(v)).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    }
  };

  const ImageCell = ({ url, label }) => {
    const u = String(url || '').trim();
    const [failed, setFailed] = useState(false);
    if (!u) {
      return (
        <span className="inline-flex h-10 w-14 items-center justify-center rounded border bg-white" title={label ? `${label} (sin foto)` : 'Sin foto'}>
          <ImageIcon className="h-5 w-5 text-gray-400" />
        </span>
      );
    }
    return (
      <a href={u} target="_blank" rel="noopener noreferrer" className="inline-flex items-center" title={label || 'Ver foto'}>
        <span className="inline-flex h-10 w-14 items-center justify-center rounded border bg-white overflow-hidden">
          {failed ? (
            <ImageIcon className="h-5 w-5 text-blue-600" />
          ) : (
            <img
              src={u}
              alt={label || 'foto'}
              className="h-10 w-14 object-cover"
              loading="lazy"
              onError={() => setFailed(true)}
            />
          )}
        </span>
      </a>
    );
  };

  const exportExcel = () => {
    const items = Array.isArray(rows) ? rows : [];
    if (!items.length) return;

    const esc = (v) => String(v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    const trs = items.map((r) => {
      const cells = [
        r.plate,
        r.date,
        (r.mileage ?? ''),
        r.fuel_type,
        r.cost_center,
        r.voucher_number,
        (r.amount ?? ''),
        r.photo_km,
        r.photo_ticket,
        r.photo_dispenser,
      ].map((c) => `<td style="border:1px solid #ddd;padding:6px;vertical-align:top;">${esc(c)}</td>`).join('');
      return `<tr>${cells}</tr>`;
    }).join('');

    const html = `<!DOCTYPE html>
<html>
<head><meta charset="utf-8" /></head>
<body>
<table style="border-collapse:collapse;font-family:Arial, sans-serif;font-size:12px;">
  <thead>
    <tr>
      <th style="border:1px solid #ddd;padding:6px;background:#f3f4f6;">Placa</th>
      <th style="border:1px solid #ddd;padding:6px;background:#f3f4f6;">Fecha</th>
      <th style="border:1px solid #ddd;padding:6px;background:#f3f4f6;">Km/Horómetro</th>
      <th style="border:1px solid #ddd;padding:6px;background:#f3f4f6;">Combustible</th>
      <th style="border:1px solid #ddd;padding:6px;background:#f3f4f6;">Centro de costo</th>
      <th style="border:1px solid #ddd;padding:6px;background:#f3f4f6;">Comprobante</th>
      <th style="border:1px solid #ddd;padding:6px;background:#f3f4f6;">Valor</th>
      <th style="border:1px solid #ddd;padding:6px;background:#f3f4f6;">Foto KM</th>
      <th style="border:1px solid #ddd;padding:6px;background:#f3f4f6;">Foto ticket</th>
      <th style="border:1px solid #ddd;padding:6px;background:#f3f4f6;">Foto dispensador</th>
    </tr>
  </thead>
  <tbody>${trs}</tbody>
</table>
</body>
</html>`;

    const blob = new Blob([html], { type: 'application/vnd.ms-excel;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const fromLabel = String(filters.from || todayYMD).trim() || todayYMD;
    const toLabel = String(filters.to || '').trim();
    const rangeLabel = (toLabel && toLabel !== fromLabel) ? `${fromLabel}_a_${toLabel}` : fromLabel;
    a.download = `control_tanqueos_${rangeLabel}.xls`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  // Top 5 por consumo relativo (galones vs kilómetros recorridos), agrupado
  // por tipo de vehículo — comparar galones totales entre una moto y una
  // grúa no es justo, así que el ranking es por tipología y por rendimiento
  // (galones cada 100 km), no por galones o dinero en bruto.
  const monthlyTopByType = useMemo(() => {
    const byPlate = new Map();
    (Array.isArray(monthlyRows) ? monthlyRows : []).forEach((row) => {
      const plate = String(row?.plate || '').trim().toUpperCase();
      if (!plate) return;
      const current = byPlate.get(plate) || {
        plate,
        vehicleType: row?.vehicle_type || null,
        totalGalones: 0,
        totalAmount: 0,
        records: 0,
        minMileage: null,
        maxMileage: null,
      };
      if (!current.vehicleType && row?.vehicle_type) current.vehicleType = row.vehicle_type;
      current.totalGalones += Number(row?.galones || 0) || 0;
      current.totalAmount += Number(row?.amount || 0) || 0;
      current.records += 1;
      if (row?.mileage !== null && row?.mileage !== undefined) {
        const m = Number(row.mileage);
        if (current.minMileage === null || m < current.minMileage) current.minMileage = m;
        if (current.maxMileage === null || m > current.maxMileage) current.maxMileage = m;
      }
      byPlate.set(plate, current);
    });

    const perVehicle = Array.from(byPlate.values()).map((item) => {
      const kmRecorridos = (item.minMileage !== null && item.maxMileage !== null) ? (item.maxMileage - item.minMileage) : null;
      const hasEnoughData = kmRecorridos !== null && kmRecorridos > 0 && item.totalGalones > 0;
      const rate100km = hasEnoughData ? (item.totalGalones / kmRecorridos) * 100 : null;
      return { ...item, vehicleType: item.vehicleType || 'Sin tipo definido', kmRecorridos, rate100km, hasEnoughData };
    });

    const byType = new Map();
    perVehicle.forEach((item) => {
      if (!byType.has(item.vehicleType)) byType.set(item.vehicleType, []);
      byType.get(item.vehicleType).push(item);
    });

    return Array.from(byType.entries()).map(([vehicleType, items]) => ({
      vehicleType,
      top: items.filter((i) => i.hasEnoughData).sort((a, b) => b.rate100km - a.rate100km).slice(0, 5),
      withoutData: items.filter((i) => !i.hasEnoughData).length,
    })).sort((a, b) => a.vehicleType.localeCompare(b.vehicleType));
  }, [monthlyRows]);

  const averageConsumptionStats = useMemo(() => {
    const items = Array.isArray(monthlyRows) ? monthlyRows : [];
    const rowsWithAmount = items.filter((row) => Number.isFinite(Number(row?.amount)) && Number(row.amount) > 0);
    const totalAmount = rowsWithAmount.reduce((acc, row) => acc + (Number(row?.amount || 0) || 0), 0);
    const totalRecords = rowsWithAmount.length;
    const uniquePlates = new Set(rowsWithAmount.map((row) => String(row?.plate || '').trim().toUpperCase()).filter(Boolean)).size;
    const monthlyAverage = totalRecords > 0 ? totalAmount / totalRecords : 0;

    const perPlate = new Map();
    rowsWithAmount.forEach((row) => {
      const plate = String(row?.plate || '').trim().toUpperCase();
      if (!plate) return;
      const current = perPlate.get(plate) || { plate, totalAmount: 0, records: 0 };
      current.totalAmount += Number(row?.amount || 0) || 0;
      current.records += 1;
      perPlate.set(plate, current);
    });

    const topAveragePlates = Array.from(perPlate.values()).map((item) => ({
      ...item,
      avgAmount: item.records > 0 ? item.totalAmount / item.records : 0,
    })).sort((a, b) => {
      if (b.avgAmount !== a.avgAmount) return b.avgAmount - a.avgAmount;
      return b.totalAmount - a.totalAmount;
    }).slice(0, 5);

    return {
      totalAmount,
      totalRecords,
      uniquePlates,
      monthlyAverage,
      topAveragePlates,
    };
  }, [monthlyRows]);

  const costCenterIndicators = useMemo(() => {
    const map = new Map();
    (Array.isArray(monthlyRows) ? monthlyRows : []).forEach((row) => {
      const center = String(row?.cost_center || '').trim() || 'Sin centro de costo';
      const plate = String(row?.plate || '').trim().toUpperCase();
      const current = map.get(center) || {
        costCenter: center,
        totalAmount: 0,
        records: 0,
        plates: new Set(),
      };
      current.totalAmount += Number(row?.amount || 0) || 0;
      current.records += 1;
      if (plate) current.plates.add(plate);
      map.set(center, current);
    });

    return Array.from(map.values()).map((item) => ({
      costCenter: item.costCenter,
      totalAmount: item.totalAmount,
      records: item.records,
      plates: item.plates.size,
      avgAmount: item.records > 0 ? item.totalAmount / item.records : 0,
    })).sort((a, b) => {
      if (b.totalAmount !== a.totalAmount) return b.totalAmount - a.totalAmount;
      return b.records - a.records;
    }).slice(0, 10);
  }, [monthlyRows]);

  return (
    <div className="bg-white rounded-xl shadow border border-gray-200 p-5">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-lg font-semibold">Control Tanqueos</div>
          <div className="text-xs text-gray-500">Información tomada del preoperacional por placa y fecha</div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setTanqueoModalTarget({})}
            className="px-3 py-2 rounded-lg text-white text-sm"
            style={{ backgroundColor: '#EA580C' }}
          >
            Nuevo tanqueo
          </button>
          <button
            type="button"
            onClick={load}
            className="px-3 py-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-sm"
            disabled={loading}
          >
            Actualizar
          </button>
          <button
            type="button"
            onClick={exportExcel}
            className="px-3 py-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-sm"
            disabled={loading || rows.length === 0}
          >
            <span className="inline-flex items-center gap-2"><Download className="w-4 h-4" /> Exportar Excel</span>
          </button>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 md:grid-cols-4 xl:grid-cols-[170px_170px_180px_1fr] gap-3">
        <div>
          <label className="block text-xs text-gray-600 mb-1">Desde</label>
          <input
            type="date"
            value={filters.from}
            onChange={(e) => {
              const v = e.target.value;
              // Si "Hasta" queda vacío o antes de "Desde", se empareja al
              // mismo día (comportamiento anterior de un solo campo) —
              // pero si el usuario ya eligió un rango, se conserva.
              setFilters((s) => ({ ...s, from: v, to: (!s.to || s.to < v) ? v : s.to }));
            }}
            className="w-full border rounded-lg px-3 py-2 bg-gray-50 text-sm"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-600 mb-1">Hasta</label>
          <input
            type="date"
            value={filters.to}
            onChange={(e) => {
              const v = e.target.value;
              setFilters((s) => ({ ...s, to: v, from: (!s.from || (v && s.from > v)) ? v : s.from }));
            }}
            className="w-full border rounded-lg px-3 py-2 bg-gray-50 text-sm"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-600 mb-1">Placa</label>
          <input
            value={filters.plate}
            onChange={(e) => setFilters((s) => ({ ...s, plate: e.target.value }))}
            className="w-full border rounded-lg px-3 py-2 bg-gray-50 text-sm"
            placeholder="Ej: ABC123"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-600 mb-1">Búsqueda</label>
          <div className="flex items-center gap-2">
            <input
              value={filters.q}
              onChange={(e) => setFilters((s) => ({ ...s, q: e.target.value }))}
              className="flex-1 border rounded-lg px-3 py-2 bg-gray-50 text-sm"
              placeholder="Filtrar por placa o usuario"
            />
            <button
              type="button"
              onClick={load}
              className="px-4 py-2 rounded-lg text-white text-sm"
              style={{ backgroundColor: '#2563EB' }}
              disabled={loading}
            >
              Aplicar
            </button>
          </div>
        </div>
      </div>

      <div className="mt-4 bg-gray-50 border border-gray-200 rounded-xl p-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-sm font-semibold text-gray-900">Top 5 vehículos con mayor consumo (por tipo)</div>
            <div className="text-xs text-gray-500">Rendimiento (galones cada 100 km) por placa en el mes seleccionado, agrupado por tipo de vehículo — no se compara una moto con una grúa.</div>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="month"
              value={filters.topMonth}
              onChange={(e) => setFilters((s) => ({ ...s, topMonth: e.target.value }))}
              className="border rounded-lg px-3 py-2 bg-white text-sm"
            />
            <button
              type="button"
              onClick={loadMonthlyTop}
              className="px-4 py-2 rounded-lg text-white text-sm"
              style={{ backgroundColor: '#F07E2D' }}
              disabled={indicatorLoading}
            >
              Consultar Top 5
            </button>
          </div>
        </div>

        <div className="mt-4 space-y-4">
          {indicatorLoading ? (
            <div className="rounded-lg border border-gray-200 bg-white px-4 py-6 text-center text-sm text-gray-600">Cargando indicador...</div>
          ) : monthlyTopByType.length === 0 ? (
            <div className="rounded-lg border border-gray-200 bg-white px-4 py-6 text-center text-sm text-gray-600">Sin registros de tanqueo para el mes seleccionado.</div>
          ) : (
            monthlyTopByType.map((group) => (
              <div key={group.vehicleType}>
                <div className="mb-2 flex items-center gap-2">
                  <div className="text-xs font-bold uppercase tracking-wide text-gray-700">{group.vehicleType}</div>
                  {group.withoutData > 0 && (
                    <div className="text-[11px] text-gray-400">({group.withoutData} vehículo(s) sin suficientes datos de km para calcular rendimiento)</div>
                  )}
                </div>
                {group.top.length === 0 ? (
                  <div className="rounded-lg border border-gray-200 bg-white px-4 py-4 text-xs text-gray-500">
                    Ningún vehículo de este tipo tiene al menos dos lecturas de kilometraje en el mes para calcular consumo por km.
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3">
                    {group.top.map((item, index) => (
                      <div key={item.plate} className="rounded-xl border border-orange-200 bg-white p-4 shadow-sm">
                        <div className="flex items-center justify-between gap-2">
                          <div className="text-xs font-semibold uppercase tracking-wide text-orange-700">Top {index + 1}</div>
                          <BarChart3 className="w-4 h-4 text-orange-500" />
                        </div>
                        <div className="mt-2 text-lg font-semibold text-gray-900">{item.plate}</div>
                        <div className="mt-3 text-xs text-gray-500">Rendimiento</div>
                        <div className="text-base font-semibold text-gray-900">{item.rate100km.toFixed(2)} gal / 100 km</div>
                        <div className="mt-2 text-xs text-gray-500">Galones totales</div>
                        <div className="text-sm text-gray-800">{item.totalGalones.toFixed(1)} gal</div>
                        <div className="mt-2 text-xs text-gray-500">Km recorridos (mes)</div>
                        <div className="text-sm text-gray-800">{item.kmRecorridos} km</div>
                        <div className="mt-2 text-xs text-gray-500">Registros de tanqueo</div>
                        <div className="text-sm text-gray-800">{item.records}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        <div className="mt-4 grid grid-cols-1 xl:grid-cols-2 gap-4">
          <div className="rounded-xl border border-blue-200 bg-white p-4 shadow-sm">
            <div className="flex items-center justify-between gap-2">
              <div>
                <div className="text-sm font-semibold text-gray-900">Consumo Promedio Por Tanqueo</div>
                <div className="text-xs text-gray-500">Promedio del mes seleccionado con ranking por placa.</div>
              </div>
              <BarChart3 className="w-4 h-4 text-blue-500" />
            </div>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="rounded-lg border border-blue-100 bg-blue-50 p-3">
                <div className="text-xs text-blue-700">Promedio mensual</div>
                <div className="mt-1 text-lg font-semibold text-blue-900">{fmtMoney(averageConsumptionStats.monthlyAverage)}</div>
              </div>
              <div className="rounded-lg border border-emerald-100 bg-emerald-50 p-3">
                <div className="text-xs text-emerald-700">Registros con valor</div>
                <div className="mt-1 text-lg font-semibold text-emerald-900">{averageConsumptionStats.totalRecords}</div>
              </div>
              <div className="rounded-lg border border-yellow-100 bg-yellow-50 p-3">
                <div className="text-xs text-yellow-700">Placas evaluadas</div>
                <div className="mt-1 text-lg font-semibold text-yellow-900">{averageConsumptionStats.uniquePlates}</div>
              </div>
            </div>

            <div className="mt-4 overflow-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="px-3 py-2 text-left">Placa</th>
                    <th className="px-3 py-2 text-right">Promedio</th>
                    <th className="px-3 py-2 text-right">Total</th>
                    <th className="px-3 py-2 text-right">Registros</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {averageConsumptionStats.topAveragePlates.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-3 py-6 text-center text-sm text-gray-500">Sin información suficiente para calcular promedios.</td>
                    </tr>
                  ) : (
                    averageConsumptionStats.topAveragePlates.map((item) => (
                      <tr key={item.plate}>
                        <td className="px-3 py-2 font-medium text-gray-900">{item.plate}</td>
                        <td className="px-3 py-2 text-right text-gray-800">{fmtMoney(item.avgAmount)}</td>
                        <td className="px-3 py-2 text-right text-gray-800">{fmtMoney(item.totalAmount)}</td>
                        <td className="px-3 py-2 text-right text-gray-800">{item.records}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="rounded-xl border border-emerald-200 bg-white p-4 shadow-sm">
            <div className="flex items-center justify-between gap-2">
              <div>
                <div className="text-sm font-semibold text-gray-900">Consumo Por Centro De Costo</div>
                <div className="text-xs text-gray-500">Acumulado del mes con cantidad de placas y promedio por registro.</div>
              </div>
              <BarChart3 className="w-4 h-4 text-emerald-500" />
            </div>

            <div className="mt-4 overflow-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="px-3 py-2 text-left">Centro de costo</th>
                    <th className="px-3 py-2 text-right">Total</th>
                    <th className="px-3 py-2 text-right">Promedio</th>
                    <th className="px-3 py-2 text-right">Registros</th>
                    <th className="px-3 py-2 text-right">Placas</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {costCenterIndicators.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-3 py-6 text-center text-sm text-gray-500">Sin registros de centro de costo para el mes seleccionado.</td>
                    </tr>
                  ) : (
                    costCenterIndicators.map((item) => (
                      <tr key={item.costCenter}>
                        <td className="px-3 py-2 text-gray-900">{item.costCenter}</td>
                        <td className="px-3 py-2 text-right text-gray-800">{fmtMoney(item.totalAmount)}</td>
                        <td className="px-3 py-2 text-right text-gray-800">{fmtMoney(item.avgAmount)}</td>
                        <td className="px-3 py-2 text-right text-gray-800">{item.records}</td>
                        <td className="px-3 py-2 text-right text-gray-800">{item.plates}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4 bg-white border border-gray-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="px-4 py-10 text-center text-sm text-gray-600">Cargando...</div>
        ) : (
          <>
            {rows.length === 0 ? (
              <div className="px-4 py-10 text-center text-sm text-gray-600">Sin registros para el rango seleccionado.</div>
            ) : (
              <div className="overflow-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-gray-600">
                    <tr>
                      <th className="px-4 py-3 text-left">Placa</th>
                      <th className="px-4 py-3 text-left">Fecha</th>
                      <th className="px-4 py-3 text-right">Km/Horómetro</th>
                      <th className="px-4 py-3 text-left">Combustible</th>
                      <th className="px-4 py-3 text-left">Centro de costo</th>
                      <th className="px-4 py-3 text-left">Comprobante</th>
                      <th className="px-4 py-3 text-right">Valor</th>
                      <th className="px-4 py-3 text-left">Foto KM</th>
                      <th className="px-4 py-3 text-left">Foto ticket</th>
                      <th className="px-4 py-3 text-left">Foto dispensador</th>
                      <th className="px-4 py-3 text-left">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {rows.map((r, idx) => (
                      <tr key={`${r.plate || ''}-${r.__dateOnly || r.date || ''}-${idx}`}>
                        <td className="px-4 py-3 font-medium">{r.plate || '-'}</td>
                        <td className="px-4 py-3 font-mono text-xs">{r.date || '-'}</td>
                        <td className="px-4 py-3 text-right font-mono">{r.mileage ?? '—'}</td>
                        <td className="px-4 py-3">{r.fuel_type || '—'}</td>
                        <td className="px-4 py-3">{r.cost_center || '—'}</td>
                        <td className="px-4 py-3 font-mono text-xs">{r.voucher_number || '—'}</td>
                        <td className="px-4 py-3 text-right font-mono">{r.amount !== null && r.amount !== undefined ? fmtMoney(r.amount) : '—'}</td>
                        <td className="px-4 py-3"><ImageCell url={r.photo_km} label="Foto KM" /></td>
                        <td className="px-4 py-3"><ImageCell url={r.photo_ticket} label="Foto ticket" /></td>
                        <td className="px-4 py-3"><ImageCell url={r.photo_dispenser} label="Foto dispensador" /></td>
                        <td className="px-4 py-3">
                          {r.id ? (
                            <button
                              type="button"
                              onClick={() => setTanqueoModalTarget(r)}
                              className="px-2.5 py-1 rounded border bg-white hover:bg-gray-50 text-xs"
                            >
                              Editar
                            </button>
                          ) : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>

      {tanqueoModalTarget && (
        <TanqueoFormModal
          row={tanqueoModalTarget}
          onClose={() => setTanqueoModalTarget(null)}
          onSaved={async () => {
            setTanqueoModalTarget(null);
            await load();
          }}
        />
      )}
    </div>
  );
}

export default function TransportesPanel({ location, showLocation = true }) {
  const displayNames = {
    'popayan': 'Popayán',
    'santander-de-quilichao': 'Santander de Quilichao',
  };
  const title = displayNames[location] || location;
  const [section, setSection] = useState('flota');
  // Reporta a Administración TI > Auditoría qué submódulo de Transportes
  // está viendo el usuario (ver X-Senerfi-Page en src/services/api.js).
  useEffect(() => {
    setCurrentPageContext(`/sede/${location || ''}/transportes/${section || ''}`);
  }, [location, section]);
  const { currentUser, getSectionPrefs } = useUserPrefs();
  const sectionPrefs = useMemo(() => getSectionPrefs(currentUser?.id), [getSectionPrefs, currentUser]);
  const allowed = useMemo(() => sectionPrefs?.['transportes'] || {}, [sectionPrefs]);
  // Igual que Contabilidad: si la sección nunca se tocó para este usuario
  // (clave ausente), se sigue mostrando — solo se oculta si quedó
  // explícitamente desmarcada (=== false) desde Administración TI.
  const isSectionAllowed = (key) => allowed[key] !== false;

  const TRANSPORTES_SECTION_ORDER = ['flota', 'preoperacionales', 'conductores', 'mantenimientos', 'control_kilometraje', 'control_tanqueos', 'asignacion_vehiculos', 'checklist_por_vehiculo', 'pesv', 'indicadores'];

  useEffect(() => {
    if (isSectionAllowed(section)) return;
    const firstAllowed = TRANSPORTES_SECTION_ORDER.find((k) => isSectionAllowed(k));
    if (firstAllowed) setSection(firstAllowed);
  }, [allowed, section]);

  return (
    <div className="text-gray-800">
      <h2 className="text-2xl font-semibold mb-2">Transportes</h2>
      {showLocation && (
        <p className="text-gray-700">Sede: {title}</p>
      )}
      <div className="mt-4 text-sm text-gray-600">
        Flota, Preoperacionales, Conductores, Mantenimientos, Control de kilometraje, Control Tanqueos, Asignación de vehículos, Checklist por vehículo, PESV e Indicadores.
      </div>

      {/* Índice de secciones estilo Almacén (sin sticky para evitar superposición al hacer scroll) */}
      <SectionIndexCards
        title="Indice de secciones"
        helperText="Visualiza de forma rapida la operacion completa de la flota y su seguimiento."
        columnsClassName="md:grid-cols-2 xl:grid-cols-3"
        className="mt-4"
        items={[
          { key: 'flota', title: 'Flota', description: 'Inventario y administracion de vehiculos', icon: Truck, gradient: 'linear-gradient(135deg, #2563EB 0%, #4F46E5 100%)', accentGradient: 'linear-gradient(180deg, #3B82F6 0%, #4F46E5 100%)', active: section === 'flota', onClick: () => setSection('flota') },
          { key: 'preoperacionales', title: 'Preoperacionales', description: 'Chequeos previos y estado de salida', icon: ClipboardCheck, gradient: 'linear-gradient(135deg, #0F766E 0%, #14B8A6 100%)', accentGradient: 'linear-gradient(180deg, #14B8A6 0%, #0F766E 100%)', active: section === 'preoperacionales', onClick: () => setSection('preoperacionales') },
          { key: 'conductores', title: 'Conductores', description: 'Control documental y base de conductores', icon: Users, gradient: 'linear-gradient(135deg, #7C3AED 0%, #A855F7 100%)', accentGradient: 'linear-gradient(180deg, #A855F7 0%, #7C3AED 100%)', active: section === 'conductores', onClick: () => setSection('conductores') },
          { key: 'mantenimientos', title: 'Mantenimientos', description: 'Historico y programacion de mantenimientos', icon: Wrench, gradient: 'linear-gradient(135deg, #EA580C 0%, #F59E0B 100%)', accentGradient: 'linear-gradient(180deg, #F97316 0%, #EA580C 100%)', active: section === 'mantenimientos', onClick: () => setSection('mantenimientos') },
          { key: 'control_kilometraje', title: 'Control kilometraje', description: 'Seguimiento por recorridos y uso', icon: Gauge, gradient: 'linear-gradient(135deg, #16A34A 0%, #84CC16 100%)', accentGradient: 'linear-gradient(180deg, #84CC16 0%, #16A34A 100%)', active: section === 'control_kilometraje', onClick: () => setSection('control_kilometraje') },
          { key: 'control_tanqueos', title: 'Control tanqueos', description: 'Registro de consumo y abastecimiento', icon: Fuel, gradient: 'linear-gradient(135deg, #0891B2 0%, #2563EB 100%)', accentGradient: 'linear-gradient(180deg, #22D3EE 0%, #2563EB 100%)', active: section === 'control_tanqueos', onClick: () => setSection('control_tanqueos') },
          { key: 'asignacion_vehiculos', title: 'Asignacion de vehiculos', description: 'Relacion entre vehiculos y responsables', icon: CarFront, gradient: 'linear-gradient(135deg, #DC2626 0%, #F97316 100%)', accentGradient: 'linear-gradient(180deg, #FB7185 0%, #DC2626 100%)', active: section === 'asignacion_vehiculos', onClick: () => setSection('asignacion_vehiculos') },
          { key: 'checklist_por_vehiculo', title: 'Checklist por vehiculo', description: 'Consulta consolidada por unidad movil', icon: ListChecks, gradient: 'linear-gradient(135deg, #0F766E 0%, #10B981 100%)', accentGradient: 'linear-gradient(180deg, #34D399 0%, #0F766E 100%)', active: section === 'checklist_por_vehiculo', onClick: () => setSection('checklist_por_vehiculo') },
          { key: 'pesv', title: 'PESV', description: 'Programas, seguimiento y evidencias de seguridad vial', icon: ShieldCheck, gradient: 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)', accentGradient: 'linear-gradient(180deg, #818CF8 0%, #4F46E5 100%)', active: section === 'pesv', onClick: () => setSection('pesv') },
          { key: 'indicadores', title: 'Indicadores', description: 'Metricas y tableros de gestion del modulo', icon: BarChart3, gradient: 'linear-gradient(135deg, #CA8A04 0%, #F59E0B 100%)', accentGradient: 'linear-gradient(180deg, #FACC15 0%, #CA8A04 100%)', active: section === 'indicadores', onClick: () => setSection('indicadores') },
        ].filter((item) => isSectionAllowed(item.key))}
      />

      {/* Contenido de sección (placeholder) */}
      {section === 'flota' && (
        <section id="flota" className="mt-4">
          <VehiclesModule />
        </section>
      )}
      {section === 'preoperacionales' && (
        <section id="preoperacionales" className="mt-4">
          <PreoperacionalesModule />
        </section>
      )}
      {section === 'conductores' && (
        <section id="conductores" className="mt-4">
          <ConductoresModule />
        </section>
      )}
      {section === 'mantenimientos' && (
        <section id="mantenimientos" className="mt-4">
          <MantenimientosModule />
        </section>
      )}
      {section === 'control_kilometraje' && (
        <section id="control_kilometraje" className="mt-4">
          <ControlKilometrajeModule />
        </section>
      )}
      {section === 'control_tanqueos' && (
        <section id="control_tanqueos" className="mt-4">
          <ControlTanqueosModule />
        </section>
      )}
      {section === 'asignacion_vehiculos' && (
        <section id="asignacion_vehiculos" className="mt-4">
          <AsignacionVehiculosModule />
        </section>
      )}
      {section === 'checklist_por_vehiculo' && (
        <section id="checklist_por_vehiculo" className="mt-4">
          <PreoperacionalesModule mode="checklist_por_vehiculo" />
        </section>
      )}
      {section === 'pesv' && (
        <section id="pesv" className="mt-4">
          <PesvModule location={location} />
        </section>
      )}
      {section === 'indicadores' && (
        <section id="indicadores" className="mt-4">
          <IndicadoresMantenimientosModule />
        </section>
      )}
    </div>
  );
}
